import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Shield,
  Lock,
  Eye,
  Heart,
  CheckCircle2,
  Users,
  Award,
  FileText,
  HelpCircle,
  ArrowRight,
  Baby,
  ShieldCheck,
  Volume2,
  Filter,
  Clock,
  UserCheck
} from "lucide-react";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";
import { useSEO, SEO_CONFIGS } from "@/hooks/useSEO";

const safetyFeatures = [
  {
    icon: ShieldCheck,
    title: "COPPA Compliant",
    description: "We comply with the Children's Online Privacy Protection Act, ensuring your child's data is protected and handled responsibly."
  },
  {
    icon: Filter,
    title: "Content Filtering",
    description: "All AI-generated stories pass through multiple safety filters to ensure age-appropriate, positive content."
  },
  {
    icon: Lock,
    title: "Data Encryption",
    description: "Your family's information is encrypted both in transit and at rest using industry-standard protocols."
  },
  {
    icon: Eye,
    title: "No Tracking Ads",
    description: "SnuggleCrafters is 100% ad-free. We never show ads or use tracking cookies for advertising purposes."
  },
  {
    icon: UserCheck,
    title: "Parent Controls",
    description: "Parents have full control over their children's profiles, story content preferences, and account settings."
  },
  {
    icon: Clock,
    title: "Safe Screen Time",
    description: "Our stories are designed to help wind down, not stimulate. Perfect for healthy bedtime routines."
  }
];

const trustBadges = [
  { label: "COPPA Compliant", icon: Shield },
  { label: "Ad-Free", icon: CheckCircle2 },
  { label: "No Data Selling", icon: Lock },
  { label: "Family Safe", icon: Heart }
];

const faqs = [
  {
    question: "How is my child's data protected?",
    answer: "We use enterprise-grade encryption and never share personal data with third parties. Child data is stored securely and only used to personalize their stories."
  },
  {
    question: "What content filters are in place?",
    answer: "Our AI uses multiple safety layers including profanity filters, violence detection, and age-appropriate content validation to ensure every story is suitable for children."
  },
  {
    question: "Can I control what my child sees?",
    answer: "Yes! You can set content preferences, manage child profiles, and review all generated stories. Parents have full visibility and control."
  },
  {
    question: "Is SnuggleCrafters COPPA compliant?",
    answer: "Absolutely. We follow all COPPA guidelines and do not collect personal information from children under 13 without verifiable parental consent."
  },
  {
    question: "Do you show advertisements to children?",
    answer: "No. SnuggleCrafters is completely ad-free. We believe children's content should be pure and uninterrupted."
  }
];

export default function ParentsPage() {
  useSEO(SEO_CONFIGS.parents);

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-violet-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <LandingNavigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 mb-6">
            <Shield className="h-4 w-4" />
            <span className="text-sm font-medium">Designed with families in mind</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Your Child's Safety is Our Priority
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            SnuggleCrafters is built from the ground up with child safety, privacy, and trust at its core.
            We're COPPA-compliant, ad-free, and committed to creating a safe digital space for your family.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {trustBadges.map((badge, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-gray-800 shadow-md"
              >
                <badge.icon className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Features Grid */}
      <section className="py-16 px-4 bg-white dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How We Keep Your Family Safe</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Every feature of SnuggleCrafters is designed with your child's wellbeing in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {safetyFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Your Privacy, Our Promise</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg">We never sell your data</h3>
                    <p className="text-gray-600 dark:text-gray-300">Your family's information stays with us. Period.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg">Minimal data collection</h3>
                    <p className="text-gray-600 dark:text-gray-300">We only collect what's needed to personalize stories.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg">Easy data deletion</h3>
                    <p className="text-gray-600 dark:text-gray-300">Request complete removal of your data at any time.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg">Transparent practices</h3>
                    <p className="text-gray-600 dark:text-gray-300">Read our clear, plain-language privacy policy.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex gap-4">
                <Link href="/privacy">
                  <Button variant="outline" className="gap-2">
                    <FileText className="h-4 w-4" />
                    Read Privacy Policy
                  </Button>
                </Link>
                <Link href="/terms">
                  <Button variant="outline" className="gap-2">
                    <FileText className="h-4 w-4" />
                    Terms of Service
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-3xl p-8">
              <div className="text-center">
                <Lock className="h-16 w-16 mx-auto text-purple-600 mb-4" />
                <h3 className="text-2xl font-bold mb-2">Enterprise-Grade Security</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Your data is protected with the same security standards used by banks and healthcare providers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 px-4 bg-white dark:bg-gray-800/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Common questions from parents about safety and privacy.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-md">
                <CardHeader>
                  <CardTitle className="text-lg flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    {faq.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 pl-8">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Create Safe, Magical Stories?</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Join thousands of families who trust SnuggleCrafters for their bedtime stories.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/auth">
              <Button size="lg" className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Link href="/help">
              <Button size="lg" variant="outline" className="gap-2">
                <HelpCircle className="h-5 w-5" />
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
